import { z } from 'zod';

const translations = z.object({
	onboarding: z
		.object({
			dragLeftRight: z
				.string()
				.describe('String contains the translation for 2D onboarding element')
				.default('Drag Left/Right')
		})
		.default({}),
	editorMode: z
		.object({
			modelLabel: z.string().describe('Translation for the table model row').default('Model'),
			colorLabel: z.string().describe('Translation for the table color row').default('Color'),
			rimLabel: z.string().describe('Translation for the table rim row').default('Rim'),
			headline: z.string().describe('Translation for the table model row').default('list of models:')
		})
		.default({}),
	global: z
		.object({
			colorLabel: z.string().describe('Translation for the color related option selector').default('Colour'),
			rimLabel: z.string().describe('Translation for the rim related option selector').default('Rim'),
			interiorLabel: z.string().describe('Translation for the interior related option selector').default('Interior'),
			testDriveFormTitle: z
				.string()
				.describe('Translation for the test drive form layer title')
				.default('Probefahrtanfrage'),
			interiorOptionLabel: z.string().describe('Translation for the interior options').optional(),
			teaser: z
				.object({
					disclaimer: z.string().optional()
				})
				.optional(),
			ecsLabel: z
				.string()
				.describe('Translation for the disclaimer related button to open efficiency popup')
				.default('Full data'),
			ariaLabel: z
				.object({
					vehicleTurntableDescription: z
						.string()
						.describe('Translation for the container of the FeatureApp')
						.default('Car Visualizing Tool'),
					close: z.string().describe('Translation for aria-label close button in disclaimer').default('Close'),
					back: z.string().describe('Translation for aria-label back button in exit interior').default('Back'),
					zoomIn: z.string().describe('Translation for aria-label zoomIn button in zoom controls').default('Zoom In'),
					zoomOut: z
						.string()
						.describe('Translation for aria-label zoomOut button in zoom controls')
						.default('Zoom out'),
					openDisclaimerLightbox: z
						.string()
						.describe('Translation for aria-label openDisclaimer button in disclaimer (ex: full data)')
						.default('Open detailed disclaimer'),
					wltpInfo: z
						.string()
						.describe('Translation for emissions WLTP data used for no-victor markets')
						.default('WLTP info'),
					closeErrorPopup: z
						.string()
						.describe('Translation for aria-label closeError button in error modal')
						.default('Close error popup'),
					activeTrimline: z
						.string()
						.describe('Translation for aria-label active trimline button in trimline selection')
						.default('Active trimline'),
					inactiveTrimline: z
						.string()
						.describe('Translation for aria-label inactive trimline button in trimline selection')
						.default('Inactive trimline'),
					trimlineDropdown: z
						.string()
						.describe('Translation for aria-label of highlighted trimline selection')
						.default('Trimline dropdown'),
					activeColor: z
						.string()
						.describe('Translation for aria-label active trimline button in color selection')
						.default('Active color'),
					inactiveColor: z
						.string()
						.describe('Translation for aria-label inactive trimline button in color selection')
						.default('Inactive color'),
					activeRim: z
						.string()
						.describe('Translation for aria-label active trimline button in rim selection')
						.default('Active rim'),
					inactiveRim: z
						.string()
						.describe('Translation for aria-label inactive trimline button in rim selection')
						.default('Inactive rim'),
					activeInterior: z
						.string()
						.describe('Translation for aria-label active interior button in scenery selection')
						.default('Active interior'),
					inactiveInterior: z
						.string()
						.describe('Translation for aria-label inactive interior button in scenery selection')
						.default('Inactive interior'),
					interiorLoaded: z
						.string()
						.describe('Translation for aria-label once interior has loaded')
						.default('Interior Loaded'),
					changeRims: z
						.string()
						.describe('Translation for aria-label of rim related option selector')
						.default('Change rims'),
					changeColor: z
						.string()
						.describe('Translation for aria-label of color related option selector')
						.default('Change color'),
					switchToInterior: z
						.string()
						.describe('Translation for aria-label of interior related option selector')
						.default('Switch to interior'),
					exitInterior: z
						.string()
						.describe('Translation for aria-label of exit interior related option selector')
						.default('Exit Interior'),
					showType6Disclaimer: z
						.string()
						.describe('Translation for aria-label disclaimer button in header (I)')
						.default('Show disclaimer'),
					hideType6Disclaimer: z
						.string()
						.describe('Translation for aria-label hide disclaimer button in hotspots')
						.default('Hide disclaimer'),
					downloadImage: z
						.string()
						.describe('Translation for aria-label download image button in sharing panel')
						.default('Download image'),
					threeDHasBeenLoaded: z
						.string()
						.describe('Translation for aria-label 3D has been loaded')
						.default('Three D has been loaded'),
					tapToInteract: z
						.string()
						.describe('Translation for aria-label of tap-to-interact button')
						.default('Touch to enter the 360-view'),
					scrollOptionsNext: z
						.string()
						.describe('Translation for aria-label of the scroll-forward arrow in the option selector')
						.default('Show more options'),
					scrollOptionsPrevious: z
						.string()
						.describe('Translation for aria-label of the scroll-back arrow in the option selector')
						.default('Show previous options')
				})
				.default({})
		})
		.describe('Object that contains the translations for all basic visualizer configuration')
		.optional(),
	disclaimers: z
		.object({
			imageFooter: z.string().describe('Translation for the disclaimer image footer'),
			imageFooterOverrides: z
				.record(z.string(), z.string())
				.describe('Per-model overrides for the disclaimer image footer, keyed by modelKey (e.g., "ES12CD")')
				.optional(),
			inline: z
				.object({
					deselectableEquipment: z
						.string()
						.describe('Translation for the disclaimer inline deselectable equipment')
						.optional(),
					efficiency: z
						.object({
							labels: z
								.object({
									efficiencyClass: z.string().optional(),
									efficiencyClassHybridWeighted: z.string().optional(),
									efficiencyClassHybridSustaining: z.string().optional(),
									emission: z.string().optional(),
									wltpConsumption: z.string().optional(),
									wltpConsumptionHybridWeighted: z.string().optional(),
									wltpConsumptionHybridSustaining: z.string().optional()
								})
								.optional(),
							separators: z
								.object({
									component: z
										.string()
										.describe('Translation for the disclaimer inline component separator')
										.default(' | '),
									consumptionToEmission: z
										.string()
										.describe('Translation for the disclaimer inline consumption to emission separator')
										.default(' and '),
									hybridSeparator: z
										.string()
										.describe('Translation for the disclaimer inline fuel to value separator')
										.default(' | ')
								})
								.optional()
						})
						.optional()
				})
				.optional()
		})
		.optional(),
	variants: z
		.record(
			z.string(),
			z
				.object({
					disclaimerData: z.string().optional(),
					teaser: z
						.object({
							disclaimer: z.string().optional()
						})
						.optional()
				})
				.default({})
		)
		.default({})
});

export const zodI18n = z.object({
	translations
});

export const zodLegacyI18n = z.object({
	translation: z
		.object({
			visualizer: translations
		})
		.passthrough()
});

export type ZodTranslations = z.infer<typeof translations>;
